export const DEFAULT_UI_LOCALE = "en" as const;
export const UNKNOWN_LANGUAGE = "und" as const;

/** Interface languages with reviewed message catalogs. Manuscript language
 * support is deliberately broader than this list. */
export const UI_LOCALE_OPTIONS = [
  { code: "en", nativeName: "English" },
  { code: "ar", nativeName: "العربية" },
  { code: "zh", nativeName: "简体中文" },
  { code: "zh-tw", nativeName: "繁體中文" },
  { code: "es", nativeName: "Español" },
  { code: "de", nativeName: "Deutsch" },
  { code: "fr", nativeName: "Français" },
  { code: "pt", nativeName: "Português" },
  { code: "hi", nativeName: "हिन्दी" },
  { code: "ko", nativeName: "한국어" },
  { code: "it", nativeName: "Italiano" },
  { code: "ru", nativeName: "Русский" },
  { code: "vi", nativeName: "Tiếng Việt" },
  { code: "ja", nativeName: "日本語" },
  { code: "nl", nativeName: "Nederlands" },
  { code: "pl", nativeName: "Polski" },
  { code: "id", nativeName: "Bahasa Indonesia" },
  { code: "ro", nativeName: "Română" },
] as const;

export type UiLocale = (typeof UI_LOCALE_OPTIONS)[number]["code"];
export type LanguageDirection = "ltr" | "rtl";

/** Locale tag used for dates and numbers in the authenticated interface. */
export function uiIntlLocale(locale: string | null | undefined): string {
  // Special case for zh-tw before languageBase
  if (locale === "zh-tw") return "zh-TW";
  
  switch (languageBase(locale)) {
    case "ar": return "ar-EG";
    case "zh": return "zh-CN";
    case "es": return "es-ES";
    case "de": return "de-DE";
    case "fr": return "fr-FR";
    case "pt": return "pt-PT";
    case "hi": return "hi-IN";
    case "ko": return "ko-KR";
    case "it": return "it-IT";
    case "ru": return "ru-RU";
    case "vi": return "vi-VN";
    case "ja": return "ja-JP";
    case "nl": return "nl-NL";
    case "pl": return "pl-PL";
    case "id": return "id-ID";
    case "ro": return "ro-RO";
    default: return "en-US";
  }
}

export function isUiLocale(value: string | null | undefined): value is UiLocale {
  return UI_LOCALE_OPTIONS.some((locale) => locale.code === value);
}

/** HTML lang attribute for locale */
export function htmlLang(locale: string | null | undefined): string {
  if (locale === "zh-tw") return "zh-TW";
  return uiIntlLocale(locale);
}

/**
 * Languages shown in the manuscript picker. This is a content-language
 * picker, not a promise that every deterministic writing tool supports every
 * language. Tool quality is enforced separately by languageSupport.ts.
 */
export const MANUSCRIPT_LANGUAGE_OPTIONS = [
  { code: "en", nativeName: "English" },
  { code: "es", nativeName: "Español" },
  { code: "fr", nativeName: "Français" },
  { code: "de", nativeName: "Deutsch" },
  { code: "pt-BR", nativeName: "Português (Brasil)" },
  { code: "it", nativeName: "Italiano" },
  { code: "hi", nativeName: "हिन्दी" },
  { code: "bn", nativeName: "বাংলা" },
  { code: "ta", nativeName: "தமிழ்" },
  { code: "te", nativeName: "తెలుగు" },
  { code: "ar", nativeName: "العربية" },
  { code: "he", nativeName: "עברית" },
  { code: "fa", nativeName: "فارسی" },
  { code: "ur", nativeName: "اردو" },
  { code: "zh-Hans", nativeName: "简体中文" },
  { code: "zh-Hant", nativeName: "繁體中文" },
  { code: "ja", nativeName: "日本語" },
  { code: "ko", nativeName: "한국어" },
  { code: "id", nativeName: "Bahasa Indonesia" },
  { code: "vi", nativeName: "Tiếng Việt" },
  { code: "th", nativeName: "ไทย" },
  { code: "tr", nativeName: "Türkçe" },
  { code: "pl", nativeName: "Polski" },
  { code: "nl", nativeName: "Nederlands" },
  { code: "ro", nativeName: "Română" },
  { code: "uk", nativeName: "Українська" },
  { code: "ru", nativeName: "Русский" },
] as const;

const RTL_BASE_LANGUAGES = new Set(["ar", "dv", "fa", "he", "ku", "ps", "sd", "ur", "yi"]);

export function canonicalizeLanguageTag(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 35) return null;
  try {
    return Intl.getCanonicalLocales(trimmed)[0] ?? null;
  } catch {
    return null;
  }
}

export function isValidLanguageTag(value: unknown): value is string {
  return value === UNKNOWN_LANGUAGE || canonicalizeLanguageTag(value) !== null;
}

/** Normalize a nullable API field. `undefined` means "use the supplied fallback". */
export function normalizeLanguageTag(
  value: unknown,
  fallback: string | null = UNKNOWN_LANGUAGE,
): string | null {
  if (value === undefined) return fallback;
  if (value === null || value === "") return null;
  if (value === UNKNOWN_LANGUAGE) return UNKNOWN_LANGUAGE;
  return canonicalizeLanguageTag(value);
}

export function languageBase(language: string | null | undefined): string {
  return (language || UNKNOWN_LANGUAGE).toLowerCase().split("-")[0];
}

/**
 * Map a UI locale or browser language tag onto a manuscript picker value.
 * This is a pre-selected answer the writer can change, not inference from prose.
 */
export function manuscriptLanguageMatching(tag: string | null | undefined): string | null {
  if (!tag || tag === UNKNOWN_LANGUAGE) return null;
  const lower = tag.trim().toLowerCase();
  if (lower === "zh-tw" || lower === "zh-hant" || lower === "zh-hk") return "zh-Hant";
  if (lower === "zh" || lower === "zh-cn" || lower === "zh-hans" || lower === "zh-sg") {
    return "zh-Hans";
  }

  const exact = MANUSCRIPT_LANGUAGE_OPTIONS.find((option) => option.code.toLowerCase() === lower);
  if (exact) return exact.code;

  const canonical = canonicalizeLanguageTag(tag);
  if (canonical) {
    const canonicalExact = MANUSCRIPT_LANGUAGE_OPTIONS.find(
      (option) => option.code.toLowerCase() === canonical.toLowerCase(),
    );
    if (canonicalExact) return canonicalExact.code;
  }

  const base = languageBase(tag);
  if (base === "pt") return "pt-BR";
  if (base === "zh") return "zh-Hans";
  return MANUSCRIPT_LANGUAGE_OPTIONS.find((option) => languageBase(option.code) === base)?.code ?? null;
}

export function languageDirection(language: string | null | undefined): LanguageDirection {
  return RTL_BASE_LANGUAGES.has(languageBase(language)) ? "rtl" : "ltr";
}

export function isEnglishLanguage(language: string | null | undefined): boolean {
  return languageBase(language) === "en";
}

export function resolveAiOutputLanguage(project: {
  manuscriptLanguage?: string | null;
  aiOutputLanguage?: string | null;
}): string | "auto" {
  const explicit = canonicalizeLanguageTag(project.aiOutputLanguage);
  if (explicit) return explicit;
  const source = canonicalizeLanguageTag(project.manuscriptLanguage);
  return source && source !== UNKNOWN_LANGUAGE ? source : "auto";
}

export function languageDisplayName(
  language: string,
  displayLocale?: UiLocale,
): string {
  const option = MANUSCRIPT_LANGUAGE_OPTIONS.find((item) => item.code.toLowerCase() === language.toLowerCase());
  if (option && !displayLocale) return option.nativeName;

  const canonical = canonicalizeLanguageTag(language);
  if (!canonical) return language;

  try {
    if (displayLocale) {
      const display = new Intl.DisplayNames([displayLocale], { type: "language" }).of(canonical);
      return display || language;
    }
    // With no display locale, preserve the selector's native-only behavior.
    const display = new Intl.DisplayNames([canonical], { type: "language" }).of(canonical);
    return display || option?.nativeName || language;
  } catch {
    return option?.nativeName || language;
  }
}

export function projectLanguageValues(project: {
  manuscriptLanguage?: string | null;
  aiOutputLanguage?: string | null;
}) {
  const manuscriptLanguage = canonicalizeLanguageTag(project.manuscriptLanguage) ?? UNKNOWN_LANGUAGE;
  const resolvedOutputLanguage = resolveAiOutputLanguage({ ...project, manuscriptLanguage });
  return {
    manuscriptLanguage,
    outputLanguage: resolvedOutputLanguage,
    direction: languageDirection(manuscriptLanguage),
  } as const;
}
